/**
 * Builds stimulus perception survey pages for a given condition.
 *
 * Called at runtime in StudyFlow to inject condition-specific pages
 * into the post-survey before passing to SurveyEngine.
 */

const { getStimulusSet } = require("./stimulus_exemplars");
const {
  POST_PERCEPTION_ITEMS,
  COMMENT_PERCEPTION_ITEMS,
  PERCEPTION_SCALE,
} = require("./perception_items");

/**
 * Replace all {{topic}} placeholders in a stimulus object's text fields.
 */
function interpolateTopic(stimulus, topicName) {
  if (!topicName) return stimulus;
  const replace = (str) =>
    typeof str === "string" ? str.replace(/\{\{topic\}\}/g, topicName) : str;

  const result = { ...stimulus };
  if (result.post) {
    result.post = { ...result.post, content: replace(result.post.content) };
  }
  if (result.comment) {
    result.comment = { ...result.comment, content: replace(result.comment.content) };
  }
  return result;
}

/**
 * Build stimulus perception pages for a participant's assigned condition.
 *
 * Returns 2 pages:
 *   1. Standalone posts — rate 2 posts (one pro, one contra)
 *   2. Post+comment pairs — rate 2 comments in context (one pro, one contra)
 *
 * @param {string} communicationStyle - "emotional" or "rational"
 * @param {string} groupFocus - "neutral", "ingroup", or "outgroup"
 * @param {string} [topicName] - Topic name for {{topic}} interpolation
 * @returns {Object[]} Array of survey page objects, empty if condition not found
 */
function buildStimulusPerceptionPages(communicationStyle, groupFocus, topicName) {
  const stimulusSet = getStimulusSet(communicationStyle, groupFocus);
  if (!stimulusSet) return [];

  const posts = stimulusSet.stimuli.filter((s) => s.type === "post");
  // Show only 1 post+comment pair to keep the survey manageable.
  // Pick the contra pair — it tests perception of a comment disagreeing with the OP,
  // which is more diagnostically interesting for the attribution dimension.
  const allPairs = stimulusSet.stimuli.filter((s) => s.type === "post_comment");
  const pairs = allPairs.length > 1
    ? [allPairs.find((s) => s.stimulusId.endsWith("_con")) || allPairs[allPairs.length - 1]]
    : allPairs;

  const pages = [];

  if (posts.length > 0) {
    pages.push({
      pageId: "stimulus_perception_posts",
      title: "Content Perception",
      instruction:
        "You will now see some posts similar to those in the discussion you just participated in. " +
        "Please rate each post on the following dimensions.",
      questions: posts.map((s) => {
        const stimulus = interpolateTopic({ post: s.post }, topicName);
        return {
          questionId: `sp_${s.stimulusId}`,
          type: "stimulus_perception",
          required: true,
          stimulus,
          stimulusType: "post",
          ratingTarget: "post",
          perceptionItems: POST_PERCEPTION_ITEMS,
          scale: PERCEPTION_SCALE,
        };
      }),
    });
  }

  if (pairs.length > 0) {
    pages.push({
      pageId: "stimulus_perception_comments",
      title: "Comment Perception",
      instruction:
        "You will now see posts with comments. " +
        "Please rate the **comment** (highlighted below each post) on the following dimensions.",
      questions: pairs.map((s) => {
        const stimulus = interpolateTopic({ post: s.post, comment: s.comment }, topicName);
        return {
          questionId: `sp_${s.stimulusId}`,
          type: "stimulus_perception",
          required: true,
          stimulus,
          stimulusType: "post_comment",
          ratingTarget: "comment",
          perceptionItems: COMMENT_PERCEPTION_ITEMS,
          scale: PERCEPTION_SCALE,
        };
      }),
    });
  }

  return pages;
}

module.exports = { buildStimulusPerceptionPages };
